import {useState} from 'react';
import {useAlerts} from 'react-native-paper-alerts';
import {useQuery} from 'react-query';
import {FirebaseFirestoreTypes} from '@react-native-firebase/firestore';

import {getApiUrl} from '../AppConfig';
import {useAppSettings} from '../components/AppSettings';
import {Fund, LimitedPartner, Property} from './useDomainData';
import {createHeaders} from './useFunctions';

export type GuestyCustomField = {
  fieldId: any;
  value: number;
  _id: string;
  path: string;
  displayName: string;
  object: 'reservation' | 'listing';
};

export type GuestyAuthInfo = {
  clientId: string;
  clientSecret: string;
  name: string;
  expireTime?: number;
  mintTime?: number; // seconds since 1970-01-01 00:00:00
  accessToken?: string;
};

export type GuestyListing = {
  _id: string;
  publicDescription: {
    access?: string;
    transit?: string;
    houseRules?: string;
    neighborhood?: string;
    summary?: string;
  };
  tags: string[];
  active: boolean;
  title: string;
  pictures?: {
    _id: string;
    sort?: number;
    original?: URL;
    large?: URL;
    caption?: string;
    regular?: URL;
    thumbnail?: URL;
    id?: string;
    height?: number;
    width?: number;
    size?: number;
    originalLink: {
      original: URL;
      large: URL;
      regular: URL;
      thumbnail: URL;
    };
  }[];
  nickname: string;
  checkInInstructions?: {
    primaryCheckIn: string;
  };
  publishedAddress?: any;
};

export type SyncAdapterStatus = {
  syncName: string;
  scannedThroughMs: number;
  lastExecutedMs: number;
  processedCountLastRun: number;
  unprocessedCountLastRun: number;
  lastExecutedStatus: string;
};

export type DataHygieneToolOutput = {
  toolName: string;
  dataItemsAffected: number;
  readOnly: boolean;
  rawOutput?: string;
};

export type GuestyReservation = {
  // Info direct from Guesty
  _id: string;
  canceledAt: number;
  createdAt: number;
  lastUpdatedAt: number;
  source: string;
  checkIn: number;
  checkOut: number;
  guest: GuestyGuest;
  listing: GuestyListing; // JSON _id + name
  customFields: GuestyCustomField[];
  status: string;
  balanceDue: number;

  // local reservation we render from Guesty info
  reservationRef?: FirebaseFirestoreTypes.DocumentReference; // stored in firestore
  reservationRefPath?: string; // used to pass ref in JSON

  // ephemeral fund/credit map if custom field was mapped to one
  creditsUsedMap?: {fund: Fund; credits: number}[];
  // ephemeral fund/LP map
  limitedPartnerMap?: {fundCode: string; lp: LimitedPartner}[];
  property?: Property;
  formattedStayDates?: string;
};

export type GuestyGuest = {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  email?: string;
};

export function useGuestyReservations(): GuestyReservation[] | undefined {
  const Alert = useAlerts();
  const appSettings = useAppSettings();
  const [seenError, setSeenError] = useState(false);
  const results = useQuery('guestyReservations', fetchGuestyReservations);
  if (results.isLoading) {
    console.log('loading guesty reservations');
    return;
  }
  if (results.isError) {
    if (!seenError) {
      Alert.alert('Error', '' + results.error, [{text: appSettings.t('OK')}]);
      setSeenError(true);
    }
    return;
  }
  console.log('returning guesty reservations');
  return results.data;
}

export async function fetchGuestyReservations(): Promise<GuestyReservation[]> {
  // console.log('fetching guesty account info for custom fields');
  const accountAPI = getApiUrl() + 'guesty/reservations';
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'GET',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  // console.log('return code from API call was ' + result.status);
  // console.log('reservations result:', JSON.stringify(data, null, 2));
  return data as GuestyReservation[];
}

export async function resyncGuestyReservation(
  reservationId: string,
): Promise<void> {
  console.log(`resynching guesty reservation ${reservationId}`);
  const accountAPI = getApiUrl() + `guesty/reservations/${reservationId}`;
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'PATCH',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  console.log('done resyncing');
  // console.log('return code from API call was ' + result.status);
  // console.log('reservations result:', JSON.stringify(data, null, 2));
}

export async function deleteGuestyReservation(
  reservation: GuestyReservation,
): Promise<void> {
  console.log(`deleting reservation ${reservation._id}`);
  const accountAPI = getApiUrl() + `guesty/reservations/${reservation._id}`;
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'DELETE',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  console.log('   done deleting');
}

export async function updateGuestyReservation(
  reservation: GuestyReservation,
): Promise<void> {
  console.log(`updating reservation ${reservation._id}`);
  const accountAPI = getApiUrl() + `guesty/reservations/${reservation._id}`;
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'POST',
    body: JSON.stringify(reservation),
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  console.log('   done updating');
}

export function useGuestyCustomFields(): GuestyCustomField[] | undefined {
  const Alert = useAlerts();
  const appSettings = useAppSettings();
  const [seenError, setSeenError] = useState(false);
  const results = useQuery('guestyCustomFields', fetchGuestyCustomFields);
  if (results.isLoading) {
    return;
  }
  if (results.isError) {
    if (!seenError) {
      // console.log('popping alert in useGuestyCustomFields');
      Alert.alert('Error', '' + results.error, [{text: appSettings.t('OK')}]);
      setSeenError(true);
    }
    return;
  }
  return results.data;
}

export async function fetchGuestyCustomFields(): Promise<GuestyCustomField[]> {
  // console.log('fetching guesty account info for custom fields');
  const accountAPI = getApiUrl() + 'guesty/helloworld';
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'GET',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  // console.log('return code from API call was ' + result.status);
  // console.log('account info result:', JSON.stringify(data, null, 2));
  return data.customFields as GuestyCustomField[];
}

export function useSyncAdapterStatus(
  syncName: string,
): SyncAdapterStatus | undefined {
  const Alert = useAlerts();
  const appSettings = useAppSettings();
  const [seenError, setSeenError] = useState(false);
  const results = useQuery(['syncAdapterStatus', syncName], () =>
    fetchSyncAdapterStatus(syncName),
  );
  if (results.isLoading) {
    return;
  }
  if (results.isError) {
    if (!seenError) {
      console.log('popping alert in useSyncAdapterStatus');
      Alert.alert('Error', '' + results.error, [{text: appSettings.t('OK')}]);
      setSeenError(true);
    }
    return;
  }
  console.log('we have sync adapter status now');
  return results.data;
}

export async function fetchSyncAdapterStatus(
  syncName: string,
): Promise<SyncAdapterStatus> {
  console.log('fetching sync adapter status for ' + syncName);
  const accountAPI = getApiUrl() + 'guesty/syncAdapter/' + syncName;

  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'GET',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  // console.log('sync adapter status result:', JSON.stringify(data, null, 2));
  return data as SyncAdapterStatus;
}

export async function saveSyncStatus(
  syncStatus: SyncAdapterStatus,
): Promise<void> {
  console.log('saving guesty sync status info');
  const accountAPI = getApiUrl() + 'guesty/syncAdapter/' + syncStatus.syncName;

  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'POST',
    body: JSON.stringify(syncStatus),
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    // console.log('There was a problem? ' + JSON.stringify(data));
    throw new Error(data.error);
  }
  // console.log('save sync status result:', JSON.stringify(data, null, 2));
}

export async function sync(syncName: string): Promise<void> {
  console.log('syncing now for job ' + syncName);
  const accountAPI = getApiUrl() + 'guesty/syncAdapter/' + syncName;

  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'PUT',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    // console.log('There was a problem? ' + JSON.stringify(data));
    throw new Error(data.error);
  }
  // console.log('save sync status result:', JSON.stringify(data, null, 2));
}

export function useGuestyAuthInfo(): GuestyAuthInfo | undefined {
  const Alert = useAlerts();
  const appSettings = useAppSettings();
  const [seenError, setSeenError] = useState(false);
  const results = useQuery('guestyAuthInfo', fetchGuestyAuthInfo);
  if (results.isLoading) {
    return;
  }
  if (results.isError) {
    if (!seenError) {
      // console.log('popping alert in useGuestyAuthinfo');
      Alert.alert('Error', '' + results.error, [{text: appSettings.t('OK')}]);
      setSeenError(true);
    }
    return;
  }
  // console.log('we have auth info now');
  return results.data;
}

export async function fetchGuestyAuthInfo(): Promise<GuestyAuthInfo> {
  // console.log('fetching guesty auth info');
  const accountAPI = getApiUrl() + 'guesty/auth';

  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'GET',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  // console.log('auth info result:', JSON.stringify(data, null, 2));
  return data as GuestyAuthInfo;
}

export async function saveGuestyAuthInfo(
  authInfo: GuestyAuthInfo,
): Promise<void> {
  // console.log('saving guesty auth info');
  const accountAPI = getApiUrl() + 'guesty/auth';

  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'PUT',
    body: JSON.stringify(authInfo),
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    // console.log('There was a problem? ' + JSON.stringify(data));
    throw new Error(data.error);
  }
  // console.log('save auth info result:', JSON.stringify(data, null, 2));
}

export async function forceTokenRefresh(): Promise<void> {
  // console.log('forcing guesty token refresh');
  const accountAPI = getApiUrl() + 'guesty/auth';
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'DELETE',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    // console.log('There was a problem? ' + JSON.stringify(data));
    throw new Error(data.error);
  }
  // console.log('delete token result:', JSON.stringify(data, null, 2));
}

export function useGuestyListings(): GuestyListing[] | undefined {
  const Alert = useAlerts();
  const appSettings = useAppSettings();
  const [seenError, setSeenError] = useState(false);
  const results = useQuery('guestyListings', fetchGuestyListings);
  if (results.isLoading) {
    return;
  }
  if (results.isError) {
    if (!seenError) {
      // console.log('popping alert in useGuestyCustomFields');
      Alert.alert('Error', '' + results.error, [{text: appSettings.t('OK')}]);
      setSeenError(true);
    }
    return;
  }
  return results.data;
}

export async function fetchGuestyListings(): Promise<GuestyListing[]> {
  console.log('fetching guesty listings');
  const accountAPI = getApiUrl() + 'guesty/listings';
  const result = await fetch(accountAPI, {
    headers: await createHeaders(),
    method: 'GET',
  });
  const data = (await result.json()).data;
  if (result.status === 500 || data.error !== undefined) {
    throw new Error(data.error);
  }
  // console.log('return code from API call was ' + result.status);
  // console.log('listings result:', JSON.stringify(data, null, 2));
  return data;
}
